import React from 'react';
import { Typography, Row, Col, Card, Divider, Space, Button, Collapse } from 'antd';
import { 
  WhatsAppOutlined, 
  MessageOutlined, 
  ShoppingCartOutlined, 
  ShopOutlined,
  UserOutlined,
  TeamOutlined,
  RocketOutlined,
  BarChartOutlined,
  ApiOutlined,
  BulbOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const Features = () => {
  // Données pour les fonctionnalités principales
  const mainFeatures = [
    {
      icon: <MessageOutlined className="text-5xl text-blue-500" />,
      title: "Automatisation des conversations",
      description: "Créez des réponses automatiques intelligentes qui s'adaptent aux questions de vos clients. Économisez du temps et offrez un service instantané 24/7.",
      details: [
        "Réponses automatiques personnalisables",
        "Détection d'intention par IA",
        "Scénarios de conversation configurables",
        "Transfert vers un agent humain quand nécessaire"
      ]
    },
    {
      icon: <ShoppingCartOutlined className="text-5xl text-blue-500" />,
      title: "Gestion des commandes",
      description: "Recevez et gérez vos commandes directement via WhatsApp. Suivez leur statut en temps réel et améliorez votre taux de conversion.",
      details: [
        "Création de commandes par messages WhatsApp",
        "Suivi des statuts en temps réel",
        "Notifications automatiques aux clients",
        "Historique complet des commandes"
      ]
    },
    {
      icon: <TeamOutlined className="text-5xl text-blue-500" />,
      title: "Gestion des livreurs",
      description: "Attribuez facilement les commandes à vos livreurs et suivez leurs livraisons en temps réel. Optimisez vos opérations de livraison.",
      details: [
        "Attribution des commandes par zone",
        "Application mobile pour les livreurs",
        "Suivi GPS des livraisons",
        "Calcul de la rémunération des livreurs"
      ]
    },
    {
      icon: <BarChartOutlined className="text-5xl text-blue-500" />,
      title: "Analyses et rapports",
      description: "Accédez à des statistiques détaillées sur vos conversations, ventes et performances. Prenez des décisions basées sur des données concrètes.",
      details: [
        "Tableaux de bord personnalisables",
        "Rapports sur les ventes et conversations",
        "Analyse des temps de réponse",
        "Identification des opportunités de vente"
      ]
    },
    {
      icon: <ApiOutlined className="text-5xl text-blue-500" />,
      title: "Intégrations",
      description: "Connectez WABot à vos outils existants comme Shopify pour synchroniser vos produits, commandes et clients automatiquement.",
      details: [
        "Intégration avec Shopify",
        "API ouverte pour développeurs",
        "Synchronisation des produits et stocks",
        "Webhooks personnalisables"
      ]
    },
    {
      icon: <ShopOutlined className="text-5xl text-blue-500" />,
      title: "Multi-boutiques",
      description: "Gérez plusieurs points de vente ou boutiques depuis une seule interface. Adaptez vos messages et offres selon la boutique.",
      details: [
        "Interface unifiée pour toutes les boutiques",
        "Statistiques comparatives",
        "Personnalisation par boutique",
        "Gestion des droits d'accès par boutique"
      ]
    }
  ];

  // Données pour les cas d'usage
  const useCases = [
    {
      title: "E-commerce",
      description: "Automatisez vos réponses aux questions fréquentes, guidez les clients dans leur parcours d'achat et suivez les commandes jusqu'à la livraison.",
      icon: <ShoppingCartOutlined />
    },
    {
      title: "Restauration",
      description: "Recevez les commandes par WhatsApp, confirmez-les automatiquement et informez les clients quand leur repas est prêt ou en route.",
      icon: <ShopOutlined />
    },
    {
      title: "Services",
      description: "Permettez à vos clients de prendre rendez-vous via WhatsApp et envoyez-leur des rappels automatiques pour réduire les no-shows.",
      icon: <UserOutlined />
    },
    {
      title: "Mode et Beauté",
      description: "Envoyez des catalogues produits interactifs, répondez aux questions sur les tailles et disponibilités, et fidélisez vos clients.",
      icon: <BulbOutlined />
    }
  ];

  return (
    <div className="features-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-400 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <Title level={1} className="text-white mb-6">
            Fonctionnalités
          </Title>
          <Paragraph className="text-lg opacity-90 max-w-2xl mx-auto">
            Découvrez comment WABot transforme la gestion de vos conversations WhatsApp et vous aide 
            à développer votre entreprise.
          </Paragraph>
        </div>
      </section>

      {/* Fonctionnalités principales */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            Toutes les fonctionnalités dont vous avez besoin
          </Title>
          
          <Row gutter={[32, 32]}>
            {mainFeatures.map((feature, index) => (
              <Col xs={24} md={12} lg={8} key={index}>
                <Card className="h-full shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-center mb-6">
                    {feature.icon}
                    <Title level={3} className="mt-4">{feature.title}</Title>
                  </div>
                  <Paragraph className="mb-6">
                    {feature.description}
                  </Paragraph>
                  <Divider />
                  <ul className="text-left pl-0">
                    {feature.details.map((detail, i) => (
                      <li key={i} className="mb-2 flex items-start">
                        <CheckCircleOutlined className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            Comment fonctionne WABot ?
          </Title>
          
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} md={12}>
              <div className="relative">
                <div className="absolute -top-6 -left-6 bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  1
                </div>
                <Card className="shadow-md mb-8">
                  <Title level={4}>Connectez votre numéro WhatsApp</Title>
                  <Paragraph>
                    En quelques minutes, intégrez votre numéro WhatsApp professionnel à la plateforme WABot. 
                    Une simple validation par QR code suffit, sans installation compliquée.
                  </Paragraph>
                </Card>
              </div>
              
              <div className="relative">
                <div className="absolute -top-6 -left-6 bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  3
                </div>
                <Card className="shadow-md">
                  <Title level={4}>Gérez vos commandes</Title>
                  <Paragraph>
                    Recevez les commandes directement via WhatsApp. Suivez leur statut, assignez-les à vos 
                    livreurs et informez automatiquement vos clients à chaque étape.
                  </Paragraph>
                </Card>
              </div>
            </Col>
            
            <Col xs={24} md={12}>
              <div className="relative">
                <div className="absolute -top-6 -left-6 bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  2
                </div>
                <Card className="shadow-md mb-8">
                  <Title level={4}>Configurez vos réponses automatiques</Title>
                  <Paragraph>
                    Créez des scénarios de réponses adaptés à votre activité. Notre assistant vous guide 
                    pour configurer les réponses aux questions fréquentes de vos clients.
                  </Paragraph>
                </Card>
              </div>
              
              <div className="relative">
                <div className="absolute -top-6 -left-6 bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  4
                </div>
                <Card className="shadow-md">
                  <Title level={4}>Analysez et optimisez</Title>
                  <Paragraph>
                    Consultez vos statistiques de performance et identifiez les opportunités d'amélioration. 
                    Optimisez continuellement votre service client et vos ventes.
                  </Paragraph>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Avantages clés */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            Les avantages clés
          </Title>
          
          <Row gutter={[32, 32]}>
            <Col xs={24} md={8}>
              <Card className="h-full shadow-md">
                <div className="text-center mb-6">
                  <RocketOutlined className="text-5xl text-blue-500" />
                  <Title level={3} className="mt-4">Gain de temps</Title>
                </div>
                <Paragraph>
                  Automatisez jusqu'à 80% de vos réponses aux clients et consacrez votre temps aux tâches 
                  à plus forte valeur ajoutée. Réduisez le temps de gestion de vos commandes de 70%.
                </Paragraph>
              </Card>
            </Col>
            
            <Col xs={24} md={8}>
              <Card className="h-full shadow-md border-blue-500" style={{ borderTopWidth: '3px' }}>
                <div className="text-center mb-6">
                  <ShoppingCartOutlined className="text-5xl text-blue-500" />
                  <Title level={3} className="mt-4">Augmentation des ventes</Title>
                </div>
                <Paragraph>
                  Nos clients constatent en moyenne une augmentation de 30% de leur taux de conversion 
                  grâce à la réactivité des réponses et au suivi personnalisé des commandes.
                </Paragraph>
              </Card>
            </Col>
            
            <Col xs={24} md={8}>
              <Card className="h-full shadow-md">
                <div className="text-center mb-6">
                  <UserOutlined className="text-5xl text-blue-500" />
                  <Title level={3} className="mt-4">Satisfaction client</Title>
                </div>
                <Paragraph>
                  Améliorez votre relation client grâce à des réponses instantanées et un suivi proactif. 
                  Nos clients rapportent une augmentation de 25% de la satisfaction client.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* Cas d'usage */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            WABot pour tous les secteurs
          </Title>
          
          <Row gutter={[32, 32]}>
            {useCases.map((useCase, index) => (
              <Col xs={24} sm={12} key={index}>
                <Card className="h-full shadow-md">
                  <div className="flex items-start mb-4">
                    <div className="bg-blue-100 text-blue-600 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                      {useCase.icon}
                    </div>
                    <div>
                      <Title level={4} className="m-0">
                        {useCase.title}
                      </Title>
                    </div>
                  </div>
                  <Paragraph>
                    {useCase.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mb-12">
            Questions fréquentes sur les fonctionnalités
          </Title>
          
          <div className="max-w-3xl mx-auto">
            <Collapse defaultActiveKey={['1']} expandIconPosition="end">
              <Panel header="Est-ce que je peux personnaliser les réponses automatiques ?" key="1">
                <Paragraph>
                  Oui, toutes les réponses automatiques sont entièrement personnalisables. Vous pouvez 
                  créer des scénarios de conversation adaptés à votre activité et modifier les messages 
                  à tout moment. Notre système utilise également l'IA pour apprendre et s'améliorer avec le temps.
                </Paragraph>
              </Panel>
              <Panel header="Comment fonctionne l'intégration avec Shopify ?" key="2">
                <Paragraph>
                  L'intégration avec Shopify vous permet de synchroniser automatiquement vos produits, 
                  stocks et commandes entre les deux plateformes. Quand un client passe une commande sur 
                  Shopify, elle apparaît dans WABot, et inversement. Vous pouvez également envoyer des 
                  catalogues produits directement via WhatsApp.
                </Paragraph>
              </Panel>
              <Panel header="Puis-je avoir plusieurs utilisateurs sur mon compte ?" key="3">
                <Paragraph>
                  Oui, selon votre plan d'abonnement. Le plan Starter inclut 1 utilisateur, le plan 
                  Business jusqu'à 3 utilisateurs, et le plan Enterprise offre un nombre illimité d'utilisateurs. 
                  Chaque utilisateur peut avoir des droits d'accès différents selon son rôle.
                </Paragraph>
              </Panel>
              <Panel header="Comment fonctionne la gestion des livreurs ?" key="4">
                <Paragraph>
                  WABot permet d'assigner automatiquement les commandes aux livreurs en fonction de leur 
                  zone géographique et disponibilité. Les livreurs reçoivent les informations de livraison 
                  sur leur application mobile et peuvent mettre à jour le statut en temps réel. Vous pouvez 
                  suivre leurs livraisons et calculer automatiquement leur rémunération.
                </Paragraph>
              </Panel>
              <Panel header="Quelles analyses et statistiques sont disponibles ?" key="5">
                <Paragraph>
                  WABot offre des tableaux de bord détaillés incluant le nombre de conversations, temps de 
                  réponse, taux de conversion, chiffre d'affaires, produits les plus vendus, performance des 
                  livreurs, et bien plus. Tous les rapports sont exportables et vous pouvez créer des vues 
                  personnalisées selon vos besoins.
                </Paragraph>
              </Panel>
            </Collapse>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <Title level={2} className="text-white mb-6">
            Prêt à transformer votre service client WhatsApp ?
          </Title>
          <Paragraph className="text-lg mb-8 max-w-2xl mx-auto">
            Rejoignez les entreprises qui utilisent WABot pour automatiser leurs conversations WhatsApp
            et booster leurs ventes.
          </Paragraph>
          <Space size="middle">
            <Link to="/register">
              <Button type="default" size="large" shape="round">
                Essayer gratuitement pendant 14 jours
              </Button>
            </Link>
            <Link to="/pricing">
              <Button ghost size="large" shape="round">
                Voir les tarifs
              </Button>
            </Link>
          </Space>
        </div>
      </section>
    </div>
  );
};

export default Features; 